import { showRequestError } from '../../utils/request-feedback';
import { PermissionControl } from '../../permissions/access';
import { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Checkbox,
    Descriptions,
    Drawer,
    Flex,
    Form,
    Input,
    InputNumber,
    message,
    Modal,
    Popconfirm,
    Select,
    Space,
    Table,
    Tag,
    Typography,
} from 'antd';
import {
    EyeOutlined,
    PlusOutlined,
    RetweetOutlined,
} from '@ant-design/icons';
import {
    createCoupon,
    createCouponCode,
    deleteCoupon,
    deleteCouponCode,
    getCoupon,
    getCoupons,
    getCouponCode,
    getCouponCodes,
    retryCouponProvisioning,
    updateCoupon,
    updateCouponCode,
} from '../../api/system';

const couponStatusOptions = [
    { value: 0, label: 'Draft', color: 'default' },
    { value: 1, label: 'Active', color: 'green' },
    { value: 2, label: 'Paused', color: 'orange' },
    { value: 3, label: 'Archived', color: 'red' },
];

const defaultCouponFilters = () => ({ statuses: [0, 1] });

const codeStatusOptions = [
    { value: 1, label: 'Active', color: 'green' },
    { value: 2, label: 'Paused', color: 'orange' },
    { value: 3, label: 'Archived', color: 'red' },
];

const providerOptions = [
    { value: 'PROMOTION_PROVIDER_STRIPE', label: 'Stripe' },
    { value: 'PROMOTION_PROVIDER_APPLE', label: 'Apple' },
    { value: 'PROMOTION_PROVIDER_GOOGLE', label: 'Google' },
];

const applicationModeOptions = [
    { value: 'code', label: 'Coupon code' },
    { value: 'automatic', label: 'Automatic discount' },
];

const isLegacyApplePromotionalCode = (offerType, codeType) => (
    codeType !== 'checkout' && offerType === 'promotional_offer'
);

const appleDeliveryText = (offerType, codeType) => {
    if (isLegacyApplePromotionalCode(offerType, codeType)) {
        return 'Promotional Offer (legacy code)';
    }
    if (codeType === 'checkout' || !offerType || offerType === 'offer_code') {
        return 'Offer Code / Promotional Offer (automatic)';
    }
    return offerType;
};

const applicationModeOf = (coupon) => (
    coupon?.applicationMode || 'code'
);

const codeEligibilityText = (coupon) => {
    if (coupon?.applicationMode === 'automatic' || coupon?.codeType === 'signup') {
        return 'First Pro only';
    }
    return 'Reusable after the current subscription lifecycle expires';
};

const isAutomaticCoupon = (coupon) => (
    applicationModeOf(coupon) === 'automatic'
);

const isArchived = (item) => Number(item?.status) === 3;

const provisioningOptions = [
    'pending',
    'provisioning',
    'ready',
    'failed',
    'compensating',
    'compensated',
];

const providerLabels = {
    PROMOTION_PROVIDER_STRIPE: 'Stripe',
    PROMOTION_PROVIDER_APPLE: 'Apple',
    PROMOTION_PROVIDER_GOOGLE: 'Google',
    1: 'Stripe',
    2: 'Apple',
    3: 'Google',
};

const provisioningColors = {
    pending: 'default',
    provisioning: 'processing',
    ready: 'success',
    failed: 'error',
    compensating: 'warning',
    compensated: 'default',
};

const compactParams = (params) => Object.fromEntries(
    Object.entries(params).filter(([, value]) => (
        value !== undefined && value !== null && value !== ''
    )),
);

const statusTag = (value, options) => {
    const item = options.find((option) => option.value === Number(value));
    return <Tag color={item?.color}>{item?.label || value}</Tag>;
};

const provisioningTag = (value) => (
    <Tag color={provisioningColors[value] || 'default'}>{value || '-'}</Tag>
);

const providerName = (value) => providerLabels[value] || String(value || '-');

const discountText = (percentOff) => {
    const off = Number(percentOff || 0) / 100;
    const payable = 100 - off;
    return `${off}% off · pay ${payable}%`;
};

const CouponCodesScreen = () => {
    const [couponForm] = Form.useForm();
    const [couponFilterForm] = Form.useForm();
    const [codeForm] = Form.useForm();
    const [codeFilterForm] = Form.useForm();

    const [coupons, setCoupons] = useState([]);
    const [couponLoading, setCouponLoading] = useState(false);
    const [couponSubmitting, setCouponSubmitting] = useState(false);
    const [couponModalOpen, setCouponModalOpen] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [couponPage, setCouponPage] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [couponFilters, setCouponFilters] = useState(defaultCouponFilters);

    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [codes, setCodes] = useState([]);
    const [codeLoading, setCodeLoading] = useState(false);
    const [codeSubmitting, setCodeSubmitting] = useState(false);
    const [codeModalOpen, setCodeModalOpen] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [codePage, setCodePage] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [codeFilters, setCodeFilters] = useState({});

    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const selectedApplicationMode = Form.useWatch('applicationMode', couponForm);
    const selectedCouponStatus = Form.useWatch('status', couponForm);

    const couponChoices = useMemo(
        () => coupons.filter((coupon) => !isAutomaticCoupon(coupon)).map((coupon) => ({
            value: coupon.id,
            label: `${coupon.name} (#${coupon.id})`,
        })),
        [coupons],
    );

    const fetchCoupons = async (
        current = couponPage.current,
        pageSize = couponPage.pageSize,
        filters = couponFilters,
    ) => {
        setCouponLoading(true);
        try {
            const result = await getCoupons(compactParams({
                'page.num': current,
                'page.size': pageSize,
                ...filters,
            }));
            const nextCoupons = result?.list || [];
            setCoupons(nextCoupons);
            setSelectedCoupon((currentCoupon) => {
                if (!currentCoupon?.id) {
                    return currentCoupon;
                }
                return nextCoupons.find((coupon) => String(coupon.id) === String(currentCoupon.id))
                    || currentCoupon;
            });
            setCouponPage({
                current,
                pageSize,
                total: Number(result?.page?.total || 0),
            });
        } catch (error) {
            showRequestError(error, 'Failed to load coupons');
        } finally {
            setCouponLoading(false);
        }
    };

    const fetchCodes = async (
        coupon = selectedCoupon,
        current = codePage.current,
        pageSize = codePage.pageSize,
        filters = codeFilters,
    ) => {
        if (!coupon?.id) {
            setCodes([]);
            setCodePage((old) => ({ ...old, current: 1, total: 0 }));
            return;
        }
        setCodeLoading(true);
        try {
            const result = await getCouponCodes(compactParams({
                'page.num': current,
                'page.size': pageSize,
                couponId: coupon.id,
                ...filters,
            }));
            setCodes(result?.list || []);
            setCodePage({
                current,
                pageSize,
                total: Number(result?.page?.total || 0),
            });
        } catch (error) {
            showRequestError(error, 'Failed to load coupon codes');
        } finally {
            setCodeLoading(false);
        }
    };

    useEffect(() => {
        fetchCoupons(1, 10, defaultCouponFilters());
    }, []);

    const submitCouponFilters = (values) => {
        const filters = compactParams(values);
        setCouponFilters(filters);
        fetchCoupons(1, couponPage.pageSize, filters);
    };

    const resetCouponFilters = () => {
        couponFilterForm.resetFields();
        const filters = defaultCouponFilters();
        setCouponFilters(filters);
        fetchCoupons(1, couponPage.pageSize, filters);
    };

    const openCreateCoupon = () => {
        setEditingCoupon(null);
        couponForm.setFieldsValue({
            name: '',
            status: 0,
            codeType: 'checkout',
            applicationMode: 'code',
            allowPreviousRedemption: true,
            percentOff: 5000,
            durationMonths: 3,
            providers: [
                'PROMOTION_PROVIDER_STRIPE',
                'PROMOTION_PROVIDER_APPLE',
                'PROMOTION_PROVIDER_GOOGLE',
            ],
        });
        setCouponModalOpen(true);
    };

    const openEditCoupon = (coupon) => {
        if (isArchived(coupon)) {
            message.info('Archived coupons cannot be edited');
            return;
        }
        setEditingCoupon(coupon);
        couponForm.setFieldsValue({
            name: coupon.name,
            status: Number(coupon.status),
            codeType: coupon.codeType,
            applicationMode: applicationModeOf(coupon),
            allowPreviousRedemption: Boolean(coupon.allowPreviousRedemption),
            percentOff: Number(coupon.percentOff),
            durationMonths: Number(coupon.durationMonths),
            providers: coupon.providers || [],
        });
        setCouponModalOpen(true);
    };

    const submitCoupon = async () => {
        if (isArchived(editingCoupon)) {
            message.error('Archived coupons cannot be edited');
            return;
        }
        const values = await couponForm.validateFields();
        setCouponSubmitting(true);
        try {
            let result;
            if (editingCoupon) {
                const payload = {
                    id: editingCoupon.id,
                    name: values.name,
                    status: values.status,
                    providers: Array.from(new Set([
                        ...(editingCoupon.providers || []),
                        ...(values.providers || []),
                    ])),
                };
                if (editingCoupon.provisioningStatus === 'pending') {
                    payload.percentOff = values.percentOff;
                    payload.durationMonths = values.durationMonths;
                    payload.allowPreviousRedemption = values.applicationMode !== 'automatic' && values.codeType === 'checkout';
                }
                result = await updateCoupon(payload);
            } else {
                result = await createCoupon({
                    name: values.name,
                    status: 0,
                    codeType: values.codeType,
                    applicationMode: values.applicationMode,
                    allowPreviousRedemption: values.applicationMode !== 'automatic' && values.codeType === 'checkout',
                    percentOff: values.percentOff,
                    durationMonths: values.durationMonths,
                    providers: values.providers,
                });
            }
            if (result === false) return;
            if (!result) {
                throw new Error('Empty coupon response');
            }
            message.success(editingCoupon ? 'Coupon updated' : 'Coupon created');
            setCouponModalOpen(false);
            await fetchCoupons();
        } catch (error) {
            if (!error?.errorFields) {
                showRequestError(error, 'Unable to save coupon');
            }
        } finally {
            setCouponSubmitting(false);
        }
    };

    const archiveCoupon = async (coupon) => {
        setCouponLoading(true);
        try {
            const result = await deleteCoupon(coupon.id);
            if (result === false) return;
            if (!result) {
                throw new Error('Empty archive response');
            }
            message.success('Coupon archived');
            if (selectedCoupon?.id === coupon.id) {
                setSelectedCoupon(null);
                setCodes([]);
            }
            await fetchCoupons();
        } catch (error) {
            showRequestError(error, 'Unable to archive coupon');
        } finally {
            setCouponLoading(false);
        }
    };

    const retryProvisioning = async (coupon, codeId, provider) => {
        const result = await retryCouponProvisioning(compactParams({
            couponId: coupon.id,
            couponCodeId: codeId,
            provider,
        }));
        if (result === false) return;
        if (!result) {
            showRequestError(undefined, 'Unable to schedule provisioning retry');
            return;
        }
        message.success('Provisioning retry scheduled');
        await fetchCoupons();
        if (selectedCoupon?.id === coupon.id) {
            await fetchCodes(coupon);
        }
    };

    const manageCodes = (coupon) => {
        if (isAutomaticCoupon(coupon)) {
            message.info('Automatic coupons do not use coupon codes');
            return;
        }
        setSelectedCoupon(coupon);
        setCodeFilters({});
        codeFilterForm.resetFields();
        fetchCodes(coupon, 1, codePage.pageSize, {});
    };

    const submitCodeFilters = (values) => {
        const filters = compactParams(values);
        setCodeFilters(filters);
        fetchCodes(selectedCoupon, 1, codePage.pageSize, filters);
    };

    const resetCodeFilters = () => {
        codeFilterForm.resetFields();
        setCodeFilters({});
        fetchCodes(selectedCoupon, 1, codePage.pageSize, {});
    };

    const openCreateCode = () => {
        setEditingCode(null);
        codeForm.setFieldsValue({
            couponId: selectedCoupon?.id,
            couponCode: '',
            status: 1,
        });
        setCodeModalOpen(true);
    };

    const openEditCode = (code) => {
        if (isArchived(code)) {
            message.info('Archived coupon codes cannot be edited');
            return;
        }
        setEditingCode(code);
        codeForm.setFieldsValue({
            couponId: code.couponId,
            couponCode: code.couponCode,
            status: Number(code.status),
        });
        setCodeModalOpen(true);
    };

    const submitCode = async () => {
        if (isArchived(editingCode)) {
            message.error('Archived coupon codes cannot be edited');
            return;
        }
        const values = await codeForm.validateFields();
        setCodeSubmitting(true);
        try {
            const result = editingCode
                ? await updateCouponCode({
                    id: editingCode.id,
                    status: values.status,
                })
                : await createCouponCode({
                    couponId: values.couponId,
                    couponCode: values.couponCode.trim().toUpperCase(),
                    appleOfferType: 'offer_code',
                    status: values.status,
                });
            if (result === false) return;
            if (!result) {
                throw new Error('Empty coupon code response');
            }
            message.success(editingCode ? 'Code updated' : 'Code created');
            setCodeModalOpen(false);
            await fetchCodes();
        } catch (error) {
            if (!error?.errorFields) {
                showRequestError(error, 'Unable to save coupon code');
            }
        } finally {
            setCodeSubmitting(false);
        }
    };

    const archiveCode = async (code) => {
        setCodeLoading(true);
        try {
            const result = await deleteCouponCode(code.id);
            if (result === false) return;
            if (!result) {
                throw new Error('Empty archive response');
            }
            message.success('Code archived');
            await fetchCodes();
        } catch (error) {
            showRequestError(error, 'Unable to archive coupon code');
        } finally {
            setCodeLoading(false);
        }
    };

    const openCouponDetail = async (coupon) => {
        setDetailLoading(true);
        setDetail({ type: 'coupon', data: coupon });
        try {
            const result = await getCoupon(coupon.id);
            if (result) {
                setDetail({ type: 'coupon', data: result });
            }
        } catch (error) {
            showRequestError(error, 'Unable to load coupon details');
        } finally {
            setDetailLoading(false);
        }
    };

    const openCodeDetail = async (code) => {
        const codeType = selectedCoupon?.codeType;
        setDetailLoading(true);
        setDetail({ type: 'code', data: code, codeType });
        try {
            const result = await getCouponCode(code.id);
            if (result) {
                setDetail({ type: 'code', data: result, codeType });
            }
        } catch (error) {
            showRequestError(error, 'Unable to load code details');
        } finally {
            setDetailLoading(false);
        }
    };

    const couponColumns = [
        {
            title: 'Coupon',
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <Space direction='vertical' size={0}>
                    <Typography.Text strong>{name}</Typography.Text>
                    <Typography.Text type='secondary'>#{record.id}</Typography.Text>
                </Space>
            ),
        },
        {
            title: 'Terms',
            key: 'terms',
            render: (_, record) => (
                <Space direction='vertical' size={0}>
                    <span>{discountText(record.percentOff)}</span>
                    <Typography.Text type='secondary'>
                        {record.durationMonths} monthly billing period(s)
                    </Typography.Text>
                </Space>
            ),
        },
        {
            title: 'Application',
            dataIndex: 'applicationMode',
            key: 'applicationMode',
            render: (_, record) => (
                <Tag color={isAutomaticCoupon(record) ? 'green' : 'blue'}>
                    {isAutomaticCoupon(record) ? 'automatic' : 'code'}
                </Tag>
            ),
        },
        {
            title: 'Code type',
            dataIndex: 'codeType',
            key: 'codeType',
            render: (value, record) => isAutomaticCoupon(record)
                ? '-'
                : <Tag color={value === 'signup' ? 'purple' : 'blue'}>{value}</Tag>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (value) => statusTag(value, couponStatusOptions),
        },
        {
            title: 'Provisioning',
            dataIndex: 'provisioningStatus',
            key: 'provisioningStatus',
            render: provisioningTag,
        },
        {
            title: 'Providers',
            dataIndex: 'providers',
            key: 'providers',
            render: (providers = []) => providers.map((provider) => (
                <Tag key={provider}>{providerName(provider)}</Tag>
            )),
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 330,
            render: (_, record) => (
                <Space wrap>
                    <PermissionControl permission="couponcodes.control.openCouponDetail.89fbefe9"><Button type='link' icon={<EyeOutlined />} onClick={() => openCouponDetail(record)}>
                        Details
                    </Button></PermissionControl>
                    <PermissionControl permission="couponcodes.control.openEditCoupon.42d8a636"><Button
                        type='link'
                        disabled={isArchived(record)}
                        onClick={() => openEditCoupon(record)}
                    >
                        Edit
                    </Button></PermissionControl>
                    <PermissionControl permission="couponcodes.control.retryProvisioning.d27e9772"><Button
                        type='link'
                        icon={<RetweetOutlined />}
                        onClick={() => retryProvisioning(record)}
                    >
                        Retry
                    </Button></PermissionControl>
                    <PermissionControl permission="couponcodes.control.archiveCoupon.f7bde9b9"><Popconfirm
                        title='Archive this coupon?'
                        description='New redemptions will stop. Existing discounted subscriptions are unchanged.'
                        onConfirm={() => archiveCoupon(record)}
                    >
                        <Button type='link' danger disabled={Number(record.status) === 3}>Archive</Button>
                    </Popconfirm></PermissionControl>
                </Space>
            ),
        },
    ];

    const codeColumns = [
        {
            title: 'Code',
            dataIndex: 'couponCode',
            key: 'couponCode',
            render: (value) => <Typography.Text copyable strong>{value}</Typography.Text>,
        },
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (value) => statusTag(value, codeStatusOptions),
        },
        {
            title: 'Provisioning',
            dataIndex: 'provisioningStatus',
            key: 'provisioningStatus',
            render: provisioningTag,
        },
        {
            title: 'Apple delivery',
            dataIndex: 'appleOfferType',
            key: 'appleOfferType',
            render: (value) => appleDeliveryText(value, selectedCoupon?.codeType),
        },
        {
            title: 'Provider states',
            dataIndex: 'providerStates',
            key: 'providerStates',
            render: (states = []) => states.map((state) => (
                <Tag
                    key={`${state.provider}-${state.offerType || 'primary'}-${state.provisioningStatus}`}
                    color={provisioningColors[state.provisioningStatus] || 'default'}
                >
                    {providerName(state.provider)}{state.offerType && state.offerType !== 'primary' ? ` · ${state.offerType}` : ''}: {state.provisioningStatus}
                </Tag>
            )),
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 280,
            render: (_, record) => (
                <Space wrap>
                    <PermissionControl permission="couponcodes.control.openCodeDetail.237a2066"><Button type='link' icon={<EyeOutlined />} onClick={() => openCodeDetail(record)}>
                        Details
                    </Button></PermissionControl>
                    <PermissionControl permission="couponcodes.control.openEditCode.dd2f5d81"><Button
                        type='link'
                        disabled={isArchived(record)}
                        onClick={() => openEditCode(record)}
                    >
                        Status
                    </Button></PermissionControl>
                    <PermissionControl permission="couponcodes.control.retryProvisioning.c6e3d798"><Button
                        type='link'
                        icon={<RetweetOutlined />}
                        onClick={() => retryProvisioning(selectedCoupon, record.id)}
                    >
                        Retry
                    </Button></PermissionControl>
                    <PermissionControl permission="couponcodes.control.archiveCode.e09d3cf3"><Popconfirm
                        title='Archive this coupon code?'
                        onConfirm={() => archiveCode(record)}
                    >
                        <Button type='link' danger disabled={Number(record.status) === 3}>Archive</Button>
                    </Popconfirm></PermissionControl>
                </Space>
            ),
        },
    ];

    const detailData = detail?.data;
    const detailStates = detail?.type === 'coupon'
        ? detailData?.providerStates
        : detailData?.providerStates;

    const providerStateColumns = [
        {
            title: 'Provider',
            dataIndex: 'provider',
            key: 'provider',
            render: providerName,
        },
        {
            title: 'Status',
            dataIndex: 'provisioningStatus',
            key: 'provisioningStatus',
            render: provisioningTag,
        },
        {
            title: 'Offer type',
            dataIndex: 'offerType',
            key: 'offerType',
            render: (value) => value || '-',
        },
        {
            title: 'External resource',
            key: 'externalResource',
            render: (_, record) => (
                <Typography.Text copyable>
                    {[record.externalOfferKey, record.externalCodeKey].filter(Boolean).join(' / ') || '-'}
                </Typography.Text>
            ),
        },
        {
            title: 'Transaction key',
            dataIndex: 'transactionOfferKey',
            key: 'transactionOfferKey',
            render: (value) => value ? <Typography.Text copyable>{value}</Typography.Text> : '-',
        },
        {
            title: 'Last verified',
            dataIndex: 'lastVerifiedAt',
            key: 'lastVerifiedAt',
            render: (value) => value || '-',
        },
        {
            title: 'Last error',
            key: 'lastError',
            render: (_, record) => record.lastErrorMessage
                ? <Typography.Text type='danger'>{record.lastErrorCode}: {record.lastErrorMessage}</Typography.Text>
                : '-',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <PermissionControl permission="couponcodes.control.retryProvisioning.cb23e18b"><Button
                    type='link'
                    icon={<RetweetOutlined />}
                    onClick={() => retryProvisioning(
                        detail?.type === 'coupon' ? detailData : selectedCoupon,
                        detail?.type === 'code' ? detailData?.id : undefined,
                        record.provider,
                    )}
                >
                    Retry
                </Button></PermissionControl>
            ),
        },
    ];

    return (
        <Space direction='vertical' size='large' style={{ width: '100%' }}>
            <Card
                title='Coupons'
                extra={(
                    <Space>
                        <PermissionControl permission="couponcodes.control.button.e37ac49e"><Button type='primary' icon={<PlusOutlined />} onClick={openCreateCoupon}>
                            New Coupon
                        </Button></PermissionControl>
                    </Space>
                )}
            >
                <Form
                    form={couponFilterForm}
                    layout='inline'
                    initialValues={defaultCouponFilters()}
                    onFinish={submitCouponFilters}
                >
                    <Form.Item name='keyword' label='Keyword'>
                        <Input allowClear placeholder='Name' />
                    </Form.Item>
                    <Form.Item name='statuses' label='Status'>
                        <Select
                            mode='multiple'
                            allowClear
                            maxTagCount='responsive'
                            placeholder='All coupons'
                            options={couponStatusOptions}
                            style={{ minWidth: 220 }}
                        />
                    </Form.Item>
                    <Form.Item name='provisioningStatus' label='Provisioning'>
                        <Select
                            allowClear
                            options={provisioningOptions.map((value) => ({ value, label: value }))}
                            style={{ width: 150 }}
                        />
                    </Form.Item>
                    <Form.Item name='applicationMode' label='Application'>
                        <Select allowClear options={applicationModeOptions} style={{ width: 180 }} />
                    </Form.Item>
                    <Form.Item name='codeType' label='Type'>
                        <Select
                            allowClear
                            options={[
                                { value: 'signup', label: 'Signup' },
                                { value: 'checkout', label: 'Checkout' },
                            ]}
                            style={{ width: 120 }}
                        />
                    </Form.Item>
                    <Form.Item name='provider' label='Provider'>
                        <Select allowClear options={providerOptions} style={{ width: 120 }} />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <PermissionControl permission="couponcodes.control.button.40d7842e"><Button type='primary' htmlType='submit'>Search</Button></PermissionControl>
                            <PermissionControl permission="couponcodes.control.button.12cfe5b9"><Button onClick={resetCouponFilters}>Reset</Button></PermissionControl>
                        </Space>
                    </Form.Item>
                </Form>
                <Table
                    style={{ marginTop: 20 }}
                    rowKey='id'
                    scroll={{ x: 1200 }}
                    loading={couponLoading}
                    columns={couponColumns}
                    dataSource={coupons}
                    pagination={{
                        current: couponPage.current,
                        pageSize: couponPage.pageSize,
                        total: couponPage.total,
                        showSizeChanger: true,
                        onChange: (current, pageSize) => fetchCoupons(current, pageSize),
                    }}
                />
            </Card>

            <Card
                title={selectedCoupon
                    ? `Coupon Codes · ${selectedCoupon.name}`
                    : 'Coupon Codes · select a coupon'}
                extra={(
                    <Space>
                        <Select
                            showSearch
                            optionFilterProp='label'
                            placeholder='Select coupon'
                            value={selectedCoupon?.id}
                            options={couponChoices}
                            style={{ width: 300 }}
                            onChange={(id) => {
                                const coupon = coupons.find((item) => item.id === id);
                                if (coupon) manageCodes(coupon);
                            }}
                        />
                        <PermissionControl permission="couponcodes.control.button.8ef430f0"><Button
                            type='primary'
                            icon={<PlusOutlined />}
                            disabled={!selectedCoupon || selectedCoupon.provisioningStatus !== 'ready'}
                            onClick={openCreateCode}
                        >
                            New Code
                        </Button></PermissionControl>
                    </Space>
                )}
            >
                <Form form={codeFilterForm} layout='inline' onFinish={submitCodeFilters}>
                    <Form.Item name='couponCode' label='Code'>
                        <Input allowClear />
                    </Form.Item>
                    <Form.Item name='status' label='Status'>
                        <Select allowClear options={codeStatusOptions} style={{ width: 120 }} />
                    </Form.Item>
                    <Form.Item name='provisioningStatus' label='Provisioning'>
                        <Select
                            allowClear
                            options={provisioningOptions.map((value) => ({ value, label: value }))}
                            style={{ width: 150 }}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <PermissionControl permission="couponcodes.control.button.838bf59b"><Button type='primary' htmlType='submit' disabled={!selectedCoupon}>Search</Button></PermissionControl>
                            <PermissionControl permission="couponcodes.control.button.305a29cb"><Button onClick={resetCodeFilters} disabled={!selectedCoupon}>Reset</Button></PermissionControl>
                        </Space>
                    </Form.Item>
                </Form>
                <Table
                    style={{ marginTop: 20 }}
                    rowKey='id'
                    scroll={{ x: 1000 }}
                    loading={codeLoading}
                    columns={codeColumns}
                    dataSource={codes}
                    locale={{ emptyText: selectedCoupon ? 'No coupon codes' : 'Select a coupon first' }}
                    pagination={{
                        current: codePage.current,
                        pageSize: codePage.pageSize,
                        total: codePage.total,
                        showSizeChanger: true,
                        onChange: (current, pageSize) => fetchCodes(
                            selectedCoupon,
                            current,
                            pageSize,
                        ),
                    }}
                />
            </Card>

            <PermissionControl permission="couponcodes.control.modal.10886e1a" actionOnly variant={(editingCoupon) ? 'update' : 'create'}><Modal
                title={editingCoupon ? 'Edit Coupon' : 'New Coupon'}
                open={couponModalOpen}
                confirmLoading={couponSubmitting}
                onOk={submitCoupon}
                onCancel={() => setCouponModalOpen(false)}
                destroyOnClose
                width={680}
            >
                <Form form={couponForm} layout='vertical' preserve={false}>
                    <Form.Item
                        name='name'
                        label='Coupon name'
                        rules={[{ required: true, whitespace: true }]}
                    >
                        <Input maxLength={100} />
                    </Form.Item>
                    <Flex gap={16}>
                        <Form.Item
                            name='applicationMode'
                            label='Application mode'
                            style={{ flex: 1 }}
                            rules={[{ required: true }]}
                        >
                            <Select
                                disabled={Boolean(editingCoupon)}
                                options={applicationModeOptions}
                                onChange={(value) => {
                                    if (value === 'automatic') {
                                        couponForm.setFieldValue('codeType', 'checkout');
                                    }
                                }}
                            />
                        </Form.Item>
                        {editingCoupon && (
                            <Form.Item name='status' label='Business status' style={{ width: 180 }}>
                                <Select options={couponStatusOptions} />
                            </Form.Item>
                        )}
                    </Flex>
                    {selectedApplicationMode !== 'automatic' && (
                        <Flex gap={16}>
                            <Form.Item name='codeType' label='Code type' style={{ flex: 1 }}>
                                <Select
                                    disabled={Boolean(editingCoupon)}
                                    options={[
                                        { value: 'signup', label: 'Signup — validate before account creation' },
                                        { value: 'checkout', label: 'Checkout — enter on Pro purchase page' },
                                    ]}
                                />
                            </Form.Item>
                        </Flex>
                    )}
                    <Flex gap={16}>
                        <Form.Item
                            name='percentOff'
                            label='Percent off (basis points)'
                            tooltip='1150 means 11.5% off; the customer pays 88.5%.'
                            rules={[{ required: true }]}
                            style={{ flex: 1 }}
                        >
                            <InputNumber
                                min={1}
                                max={10000}
                                disabled={editingCoupon && editingCoupon.provisioningStatus !== 'pending'}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name='durationMonths'
                            label='Discounted monthly periods'
                            rules={[{ required: true }]}
                            style={{ flex: 1 }}
                        >
                            <InputNumber
                                min={1}
                                max={12}
                                disabled={editingCoupon && editingCoupon.provisioningStatus !== 'pending'}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Flex>
                    <Form.Item
                        name='providers'
                        label='Providers'
                        extra={editingCoupon
                            ? 'Set status to Draft to add providers. Existing providers cannot be removed; new providers are provisioned asynchronously.'
                            : undefined}
                        rules={[{ required: true, message: 'Select at least one provider' }]}
                    >
                        <Checkbox.Group
                            disabled={Boolean(editingCoupon) && Number(selectedCouponStatus) !== 0}
                            options={providerOptions.map((option) => ({
                                ...option,
                                disabled: Boolean(editingCoupon)
                                    && (editingCoupon.providers || []).includes(option.value),
                            }))}
                        />
                    </Form.Item>
                </Form>
            </Modal></PermissionControl>

            <PermissionControl permission="couponcodes.control.modal.8a754135" actionOnly variant={(editingCode) ? 'update' : 'create'}><Modal
                title={editingCode ? 'Update Coupon Code Status' : 'New Coupon Code'}
                open={codeModalOpen}
                confirmLoading={codeSubmitting}
                onOk={submitCode}
                onCancel={() => setCodeModalOpen(false)}
                destroyOnClose
            >
                <Form form={codeForm} layout='vertical' preserve={false}>
                    <Form.Item name='couponId' label='Coupon ID'>
                        <Input disabled />
                    </Form.Item>
                    <Form.Item
                        name='couponCode'
                        label='Coupon code'
                        rules={[
                            { required: true },
                            {
                                pattern: /^[A-Za-z0-9]{1,64}$/,
                                message: 'Use 1–64 letters or numbers',
                            },
                        ]}
                    >
                        <Input
                            disabled={Boolean(editingCode)}
                            maxLength={64}
                            style={{ textTransform: 'uppercase' }}
                        />
                    </Form.Item>
                    <Form.Item name='status' label='Business status' rules={[{ required: true }]}>
                        <Select options={codeStatusOptions} />
                    </Form.Item>
                </Form>
            </Modal></PermissionControl>

            <Drawer
                title={detail?.type === 'coupon' ? 'Coupon details' : 'Coupon code details'}
                open={Boolean(detail)}
                loading={detailLoading}
                onClose={() => setDetail(null)}
                width={900}
            >
                {detailData && (
                    <Space direction='vertical' size='large' style={{ width: '100%' }}>
                        <Descriptions bordered column={2}>
                            <Descriptions.Item label='ID'>{detailData.id}</Descriptions.Item>
                            <Descriptions.Item label='Name / Code'>
                                {detailData.name || detailData.couponCode}
                            </Descriptions.Item>
                            <Descriptions.Item label='Business status'>
                                {statusTag(
                                    detailData.status,
                                    detail?.type === 'coupon'
                                        ? couponStatusOptions
                                        : codeStatusOptions,
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label='Provisioning'>
                                {provisioningTag(detailData.provisioningStatus)}
                            </Descriptions.Item>
                            {detail?.type === 'coupon' && (
                                <>
                                    <Descriptions.Item label='Code type'>
                                        {isAutomaticCoupon(detailData) ? '-' : detailData.codeType}
                                    </Descriptions.Item>
                                    <Descriptions.Item label='Application mode'>
                                        {isAutomaticCoupon(detailData) ? 'automatic' : 'code'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label='Eligibility'>
                                        {codeEligibilityText(detailData)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label='Terms'>
                                        {discountText(detailData.percentOff)} for {detailData.durationMonths} month(s)
                                    </Descriptions.Item>
                                </>
                            )}
                            {detail?.type === 'code' && (
                                <>
                                    <Descriptions.Item label='Coupon ID'>
                                        {detailData.couponId}
                                    </Descriptions.Item>
                                    <Descriptions.Item label='Apple delivery'>
                                        {appleDeliveryText(detailData.appleOfferType, detail.codeType)}
                                    </Descriptions.Item>
                                </>
                            )}
                            <Descriptions.Item label='Created'>{detailData.createdAt || '-'}</Descriptions.Item>
                            <Descriptions.Item label='Updated'>{detailData.updatedAt || '-'}</Descriptions.Item>
                        </Descriptions>
                        <Table
                            rowKey={(record) => `${record.provider}-${record.offerType || 'primary'}-${record.externalOfferKey || record.externalCodeKey || 'none'}`}
                            size='small'
                            pagination={false}
                            columns={providerStateColumns}
                            dataSource={detailStates || []}
                            scroll={{ x: 1000 }}
                        />
                    </Space>
                )}
            </Drawer>
        </Space>
    );
};

export default CouponCodesScreen;
